import { useEffect, useState } from "react";
import { ShoppingBag, Plus, Minus, Loader2, X, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useCheckout } from "@/lib/useCheckout";

export default function ProductGrid({ category, title, eyebrow, blurb }) {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cart, setCart] = useState({});
  const [scents, setScents] = useState([]);
  const [chosenScent, setChosenScent] = useState({}); // { productId: scent }
  const { buy, loadingId } = useCheckout();

  useEffect(() => {
    let mounted = true;
    Promise.all([
      api.get(`/products`, { params: category ? { category } : {} }),
      api.get(`/scents`).catch(() => ({ data: { scents: [] } })),
    ])
      .then(([pr, sr]) => {
        if (!mounted) return;
        const sorted = [...(pr.data.products || [])].sort((a, b) => b.price - a.price);
        setProducts(sorted);
        setScents(sr.data.scents || []);
      })
      .catch(() => toast.error("Couldn't load products."))
      .finally(() => mounted && setLoading(false));
    return () => {
      mounted = false;
    };
  }, [category]);

  const addToCart = (id) => {
    const p = products.find((x) => x.id === id);
    if (p?.scent_required && !chosenScent[id]) {
      toast.error(`Please choose a scent for ${p.name}.`);
      return;
    }
    setCart((c) => ({ ...c, [id]: (c[id] || 0) + 1 }));
  };
  const removeFromCart = (id) =>
    setCart((c) => {
      const next = { ...c, [id]: (c[id] || 0) - 1 };
      if (next[id] <= 0) delete next[id];
      return next;
    });

  const items = Object.entries(cart).map(([product_id, quantity]) => ({
    product_id,
    quantity,
    ...(chosenScent[product_id] ? { variant: chosenScent[product_id] } : {}),
  }));

  const cartCount = items.reduce((s, i) => s + i.quantity, 0);
  const cartTotal = products
    .filter((p) => cart[p.id])
    .reduce((s, p) => s + p.price * cart[p.id], 0);

  const handleCheckout = async () => {
    if (items.length === 0) {
      toast.error("Your cart is empty.");
      return;
    }
    await buy(null, "your cart", { items, kind: "product", loadingKey: "cart" });
  };

  return (
    <section className="bg-brand-cream pt-16 pb-32" data-testid={`shop-${category || "all"}-page`}>
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="max-w-2xl mb-12">
          <span className="text-xs uppercase tracking-[0.35em] text-brand-gold">
            {eyebrow}
          </span>
          <h1 className="mt-3 font-serif text-5xl lg:text-6xl text-brand-moss leading-tight">
            {title}
          </h1>
          {blurb && (
            <p className="mt-5 text-lg text-brand-moss/75 leading-relaxed">
              {blurb}
            </p>
          )}
        </div>

        {loading ? (
          <div className="flex items-center gap-2 text-brand-moss/60">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading products…
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {products.map((p) => (
              <article
                key={p.id}
                data-testid={`product-${p.id}`}
                className={`bg-brand-parchment rounded-3xl overflow-hidden border border-brand-moss/10 hover:-translate-y-1 transition-transform ${
                  p.coming_soon ? "relative" : ""
                }`}
              >
                <div className="relative aspect-[4/5] w-full overflow-hidden">
                  <div
                    className={`absolute inset-0 bg-cover bg-center transition-all duration-500 ${
                      p.coming_soon ? "opacity-30 blur-[2px] saturate-50" : ""
                    }`}
                    style={{ backgroundImage: `url(${p.image})` }}
                  />
                  {p.coming_soon && (
                    <div
                      data-testid={`coming-soon-overlay-${p.id}`}
                      className="absolute inset-0 flex flex-col items-center justify-center bg-brand-parchment/40 backdrop-blur-[1px]"
                    >
                      <span className="text-[10px] uppercase tracking-[0.4em] text-[#B7574E] mb-2">
                        In the Studio
                      </span>
                      <span className="font-serif text-3xl lg:text-4xl text-brand-moss tracking-wide leading-none text-center px-4">
                        Coming
                        <br />
                        <em className="italic text-[#B7574E]">Soon</em>
                      </span>
                      <span className="mt-3 h-px w-16 bg-brand-gold" />
                      <span className="mt-3 text-[10px] uppercase tracking-[0.35em] text-brand-moss/70">
                        Join the waitlist
                      </span>
                    </div>
                  )}
                </div>
                <div className="p-6">
                  <span className="text-[10px] uppercase tracking-[0.3em] text-brand-gold">
                    {p.tagline}
                  </span>
                  <h3 className="mt-2 font-serif text-2xl text-brand-moss leading-tight">
                    {p.name}
                  </h3>
                  <p className="mt-2 text-sm text-brand-moss/70 line-clamp-3">
                    {p.description}
                  </p>
                  {p.scent_required && !p.coming_soon && (
                    <label className="mt-4 block">
                      <span className="text-[10px] uppercase tracking-[0.3em] text-brand-moss/60 flex items-center gap-1.5 mb-1.5">
                        <Sparkles className="w-3 h-3" /> Choose your scent
                      </span>
                      <select
                        data-testid={`scent-${p.id}`}
                        value={chosenScent[p.id] || ""}
                        onChange={(e) =>
                          setChosenScent((s) => ({ ...s, [p.id]: e.target.value }))
                        }
                        className="w-full bg-white border border-brand-moss/15 rounded-xl px-3 py-2 text-sm text-brand-moss focus:outline-none focus:border-brand-gold"
                      >
                        <option value="">— Select a scent —</option>
                        {scents.map((s) => (
                          <option key={s} value={s}>
                            {s}
                          </option>
                        ))}
                      </select>
                    </label>
                  )}
                  <div className="mt-5 flex items-center justify-between">
                    <span className="font-serif text-2xl text-brand-moss">
                      ${p.price.toFixed(2)}
                    </span>
                    {p.coming_soon ? (
                      <span className="inline-flex items-center gap-1 bg-[#B7574E]/10 text-[#B7574E] border border-[#B7574E]/25 rounded-full px-3 py-1.5 text-[11px] uppercase tracking-[0.25em]" data-testid={`coming-soon-${p.id}`}>
                        Coming Soon
                      </span>
                    ) : cart[p.id] ? (
                      <div className="flex items-center gap-3 bg-brand-moss text-brand-gold rounded-full px-3 py-1.5">
                        <button
                          onClick={() => removeFromCart(p.id)}
                          data-testid={`decrement-${p.id}`}
                          className="hover:scale-110 transition"
                        >
                          <Minus className="w-4 h-4" />
                        </button>
                        <span className="text-sm w-5 text-center">{cart[p.id]}</span>
                        <button
                          onClick={() => addToCart(p.id)}
                          data-testid={`increment-${p.id}`}
                          className="hover:scale-110 transition"
                        >
                          <Plus className="w-4 h-4" />
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={() => addToCart(p.id)}
                        data-testid={`add-${p.id}`}
                        className="inline-flex items-center gap-2 bg-brand-moss text-brand-gold hover:bg-brand-moss-light px-5 py-2.5 rounded-full text-sm transition-colors"
                      >
                        <Plus className="w-4 h-4" /> Add
                      </button>
                    )}
                  </div>
                </div>
              </article>
            ))}
          </div>
        )}
      </div>

      {/* Floating cart bar */}
      {cartCount > 0 && (
        <div
          data-testid="cart-bar"
          className="fixed bottom-6 left-1/2 -translate-x-1/2 z-30 bg-brand-moss text-brand-cream rounded-full shadow-2xl flex items-center gap-4 pl-6 pr-3 py-3"
        >
          <ShoppingBag className="w-4 h-4 text-brand-gold" />
          <span className="text-sm">
            {cartCount} item{cartCount > 1 ? "s" : ""} ·{" "}
            <span className="text-brand-gold">${cartTotal.toFixed(2)}</span>
          </span>
          <button
            onClick={handleCheckout}
            disabled={loadingId === "cart"}
            data-testid="checkout-btn"
            className="bg-brand-gold text-brand-moss hover:bg-brand-gold-light px-5 py-2 rounded-full text-sm transition-colors disabled:opacity-60"
          >
            {loadingId === "cart" ? "Opening…" : "Checkout"}
          </button>
          <button
            onClick={() => setCart({})}
            aria-label="Clear cart"
            data-testid="clear-cart-btn"
            className="text-brand-cream/60 hover:text-brand-cream"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}
    </section>
  );
}
